import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { Col, Row, Button, ButtonGroup } from 'react-bootstrap';
import { PropTypes } from 'prop-types';
import path from 'path';
import LoadingPage from '../LoadingPage';
import ApiService from '../../services/ApiService';
import Form from '../form/Form';
import ClassicScreen from '../ClassicScreen';
import PdfObject from '../PdfObject';
import ClipLoader from 'react-spinners/ClipLoader';
import FileService from '../../services/FileService';
import { useLocalStorage } from '../../util/hooks';

const ViewInvoice = ({ lookupKey, setTitle }) => {
  const [fetchingPdf, setFetchingPdf] = useState(false);
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState('');
  const [layout, setLayout] = useState([]);
  const [sent, setSent] = useState(false);
  const [status, setStatus] = useState('');
  const [pdf, setPdf] = useState('');
  // const [view, setView] = useState('classic');

  // User preference
  const [view, setView] = useLocalStorage('invoiceViewToggle', 'classic');

  const submit = async payload => {
    return ApiService.post(`invoice/${encodeURIComponent(lookupKey)}/send`, payload);
  };

  const fetchPdf = async () => {
    setFetchingPdf(true);

    const response = await ApiService.get(`invoice/${encodeURIComponent(lookupKey)}/stream`);
    const pdfUrl = response.data;
    if (response) {
      if (process.env.NODE_ENV === 'development') {
        setPdf(pdfUrl);
      } else {
        setPdf(FileService.downloadPath(path.basename(pdfUrl)));
      }
    }

    setFetchingPdf(false);
  };

  const callback = () => {
    setSent(true);
  };

  const getEmails = async () => {
    const response = await ApiService.get(`invoice/${encodeURIComponent(lookupKey)}/emails`);
    const { emails, printers, status } = response.data;
    setStatus(status);

    if (status === 'closed') {
      fetchPdf();
    }

    const initialLayout = [
      {
        name: 'email',
        label: 'Email',
        type: 'email',
        validation: {
          type: 'static',
          suggestions: emails,
          optional: true,
          allowSelectAll: true,
        },
        data: [],
        control: 'Tags',
      },
      {
        name: 'printer',
        label: 'Printer',
        options: printers,
        data: '',
      },
      {
        name: 'message',
        control: 'Textarea',
        label: 'Message',
      },
    ];

    setLayout(initialLayout);
  };

  const getData = async () => {
    await getEmails();
    const response = await ApiService.get(`drilldown/viewAndPrintInvoice/${encodeURIComponent(lookupKey)}`);
    const { data } = response.data;

    let invoice = data;
    if (Array.isArray(data)) {
      invoice = data.join('\n');
    }

    setInvoice(invoice);
    setLoading(false);
    setTitle(lookupKey);
  };

  const toggleView = () => {
    if (view === 'pdf') {
      setView('classic');
      return;
    }

    setView('pdf');
  };

  const renderPdf = () => {
    if (fetchingPdf) {
      return <LoadingPage />;
    }

    return <PdfObject url={pdf} width="100%" height="800" />;
  };

  useEffect(() => {
    getData();
  }, []);

  if (loading) {
    return (
      <div className="view-invoice d-flex flex-column">
        <LoadingPage message="Fetching Invoice" />
      </div>
    );
  }

  if (status !== 'closed') {
    return (
      <Row className="view-invoice">
        <Col className="invoice">
          <ClassicScreen>{invoice}</ClassicScreen>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="view-invoice">
      <Col lg={8} className="invoice">
        {fetchingPdf === true && (
          <div className="loading-pdf">
            <ClipLoader size={15} color="#fff" /> <span className="pl-1">Loading PDF</span>
          </div>
        )}
        {view === 'pdf' ? renderPdf() : <ClassicScreen>{invoice}</ClassicScreen>}
      </Col>
      <Col lg={4} className="email-form">
        {sent ? (
          <div className="email-sent">
            <i className="fad fa-check-circle" />
            Invoice successfully sent!
            <div>
              <Button onClick={() => setSent(false)} variant="secondary">
                Dismiss
              </Button>
            </div>
          </div>
        ) : (
          <>
            <ButtonGroup className="m-3" block>
              <Button variant="secondary" onClick={toggleView} disabled={view === 'pdf'}>
                {view === 'pdf' && <i className="fas fa-check" style={{ color: 'var(--green-5)' }} />} PDF
              </Button>
              <Button variant="secondary" onClick={toggleView} disabled={view === 'classic'}>
                {view === 'classic' && <i className="fas fa-check" style={{ color: 'var(--green-5)' }} />} Text
              </Button>
            </ButtonGroup>
            <div className="email-head">Fill out this form to email invoice to customer</div>
            <Form
              layout={layout}
              submit={submit}
              submitCallback={callback}
              feedback="none"
              hideCancel
              resetOnSubmit
              exportFormat="object"
            />
          </>
        )}
      </Col>
    </Row>
  );
};

ViewInvoice.propTypes = {
  setTitle: PropTypes.func,
  lookupKey: PropTypes.string,
};

export default ViewInvoice;
